import { createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../services/api.service';
import { Toast } from '../../components/Common/Toast';
import { HTTPStatusEnum } from '../../interfaces/log.interface';
import { setActiveClassroomProjects, setLimitClassroomStudents } from '../userSlice';
import { SelectedDateTime } from '../../pages/Teacher/components/PublishingTimelineForm';
import { localDateToUTCKeepSame } from '../../helper/helper'; // Async Thunk for

// Async Thunk for getting list of classrooms for Teacher
export const getTeacherClassrooms = createAsyncThunk(
  'user/getTeacherClassrooms',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await callAPI('api/classrooms');
      if (response?.status !== HTTPStatusEnum.OK) {
        Toast(response?.message || 'Unexpected Error Occurred', true);
        return rejectWithValue(response?.message || 'Failed to get classrooms');
      }
      dispatch(setLimitClassroomStudents(+(response?.studentsLimit || 0)));
      return response?.list ?? [];
    } catch (error: any) {
      Toast('Failed to get list of classrooms', true);
      return rejectWithValue(error.message);
    }
  },
);

// Async Thunk for getting list of classrooms for Teacher by admin
export const getTeacherClassroomsByAdmin = createAsyncThunk(
  'user/getTeacherClassroomsByAdmin',
  async ({ teacherEmail }: { teacherEmail: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await callAPI(`admin/user/classrooms?teacherEmail=${teacherEmail}`);
      if (response?.status !== HTTPStatusEnum.OK) {
        Toast(response?.message || 'Unexpected Error Occurred', true);
        return rejectWithValue(response?.message || 'Failed to get classrooms');
      }
      return response?.list ?? [];
    } catch (error: any) {
      Toast('Failed to get list of classrooms', true);
      return rejectWithValue(error.message);
    }
  },
);

// Async Thunk for getting list of classrooms for Teacher by admin
export const setProjectAsDefaultByAdmin = createAsyncThunk(
  'user/setProjectAsDefaultByAdmin',
  async ({ classroomId, projectId }: { classroomId: string; projectId: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await callAPI(
        `admin/user/classrooms/${classroomId}/projects/${projectId}/setDefault`,
        {},
        'POST',
      );
      if (response?.status !== HTTPStatusEnum.OK) {
        Toast(response?.message || 'Unexpected Error Occurred', true);
        return null;
      }
      Toast('The selected project has been successfully set as the default.');
      return { classroomId, projectId };
    } catch (error: any) {
      Toast('Failed to set default project by admin', true);
      rejectWithValue(error.message);
    }
  },
);

// Async Thunk for getting list of classrooms for Teacher by admin
export const updateStartDateProjectByAdmin = createAsyncThunk(
  'user/updateStartDateProjectByAdmin',
  async (
    { projectId, startDateData }: { projectId: string; startDateData: SelectedDateTime },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await callAPI(
        `admin/user/classrooms/projects/${projectId}`,
        {
          startDate: startDateData.startDate ? localDateToUTCKeepSame(startDateData.startDate) : null,
          startDateTime: startDateData.startDateTime,
        },
        'POST',
      );
      if (response?.status !== HTTPStatusEnum.OK) {
        Toast(response?.message || 'Unexpected Error Occurred', true);
      } else {
        Toast('Start date successfully updated');
      }
    } catch (error: any) {
      Toast('Failed to get list of classrooms', true);
      rejectWithValue(error.message);
    }
  },
);

// Async Thunk for getting list of classrooms projects for Teacher
export const getTeacherClassroomProjects = createAsyncThunk(
  'user/getTeacherClassroomProjects',
  async (classroomId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await callAPI(`api/classrooms/${classroomId}/projects`);
      if (response?.status !== HTTPStatusEnum.OK) {
        Toast(response?.message || 'Unexpected Error Occurred', true);
      }
      return dispatch(setActiveClassroomProjects({ projects: response?.list || [] }));
    } catch (error: any) {
      Toast('Failed to get list of classroom projects', true);
      dispatch(setActiveClassroomProjects({ projects: [] }));
      rejectWithValue(error.message);
    }
  },
);
